import React, { useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { Input } from "reactstrap";
import api from "../../api/axios";
import KpiTile from "../../../components/dashboard/KpiTile";
import ChartFrame from "../../../components/dashboard/ChartFrame";

// The product's PAD category colors — each chart carries ONE of them (identity stays
// with the metric, matching the PAD report's section bars), never a many-hue stack.
const HUES = {
  incentives: "#1565C0",
  delays: "#EF6C00",
  skipped: "#C62828",
  tempStops: "#8D6E63",
  newPads: "#2E7D32",
};

const money = (n) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(Number(n) || 0);

const currentYm = () => new Date().toISOString().slice(0, 7);

const monthLabel = (ym) => {
  if (!/^\d{4}-\d{2}$/.test(String(ym || ""))) return "";
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString("en-CA", { month: "short", year: "numeric" });
};

/** One horizontal single-hue bar chart: properties on the axis, one measure. */
const PropertyBars = ({ rows, hue, valueFormatter }) => {
  const data = rows.map((r) => r.value);
  const labels = rows.map((r) => r.name);
  const options = useMemo(
    () => ({
      chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit", parentHeightOffset: 0 },
      colors: [hue],
      plotOptions: { bar: { horizontal: true, barHeight: "52%", borderRadius: 4, borderRadiusApplication: "end" } },
      dataLabels: {
        enabled: true,
        formatter: valueFormatter || ((v) => v),
        style: { fontSize: "11px", fontWeight: 600, colors: ["#3D4C63"] },
        offsetX: 24,
      },
      xaxis: { categories: labels, labels: { style: { colors: "#8A94A6", fontSize: "11px" } }, axisBorder: { show: false }, axisTicks: { show: false } },
      yaxis: { labels: { style: { colors: "#3D4C63", fontSize: "12px", fontWeight: 500 }, maxWidth: 180 } },
      grid: { borderColor: "#EDF1F7", strokeDashArray: 3, xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
      tooltip: { y: { formatter: valueFormatter || ((v) => v), title: { formatter: () => "" } } },
      states: { hover: { filter: { type: "lighten", value: 0.06 } } },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [labels.join("|"), hue]
  );
  return (
    <ReactApexChart
      type="bar"
      height={Math.max(120, rows.length * 44 + 40)}
      series={[{ name: "", data }]}
      options={options}
    />
  );
};

const topRows = (properties, key, limit = 8) =>
  properties
    .map((p) => ({ name: p.property_name, value: key === "incentives_total" ? Math.round(p[key]) : p[key] }))
    .filter((r) => r.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, limit);

/**
 * The dashboard's PAD overview: one month across every property the viewer can see.
 * Largest incentives, PAD delays, temporary stops in force, and skipped pulls — as
 * single-measure property comparisons (one hue each), never a many-series stack.
 */
const PadOverview = () => {
  const [month, setMonth] = useState(currentYm);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    api
      .get("v2/pad-info/dashboard", { params: { month } })
      .then((resp) => {
        if (!active) return;
        if (!resp?.data?.success) throw new Error(resp?.data?.message || "Failed to load");
        setData(resp.data.data);
      })
      .catch((err) => {
        if (!active) return;
        // 403 = a role without PAD access opened the dashboard; the section just stays quiet.
        if (err?.response?.status === 403) { setData(null); setError("forbidden"); return; }
        setError(err?.response?.data?.message || err.message || "Failed to load the PAD overview");
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [month]);

  if (error === "forbidden") return null;

  const t = data?.totals;
  const properties = data?.properties || [];
  const incentiveBars = topRows(properties, "incentives_total");
  const delayBars = topRows(properties, "delays");
  const skippedBars = topRows(properties, "skipped_pulls");
  const tempStops = data?.temp_stop_rows || [];
  const topIncentives = (data?.incentive_rows || []).slice(0, 6);

  return (
    <section className="rdc-section" aria-label="PAD overview">
      <style>{`
        .pad-ov-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; }
        @media (max-width: 1100px) { .pad-ov-grid { grid-template-columns: 1fr; } }
        .pad-ov-list { font-size: 12.5px; color: #3D4C63; margin: 0; padding: 0; list-style: none; max-height: 250px; overflow-y: auto; }
        .pad-ov-list li { display: flex; justify-content: space-between; gap: 10px; padding: 7px 2px; border-bottom: 1px dashed #EDF1F7; }
        .pad-ov-list li:last-child { border-bottom: none; }
        .pad-ov-list .who { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .pad-ov-list .amt { font-weight: 700; white-space: nowrap; font-variant-numeric: tabular-nums; }
        .pad-ov-kpis { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 12px; margin-bottom: 16px; }
        @media (max-width: 1100px) { .pad-ov-kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
      `}</style>
      <header className="rdc-section__head">
        <div className="rdc-section__titles">
          <span className="rdc-section__eyebrow">Pre-authorized debits</span>
          <h2 className="rdc-section__title">PAD overview</h2>
          <p className="rdc-section__subtitle">
            Incentives, delays, and skipped pulls across your properties · {monthLabel(month)}
          </p>
        </div>
        <Input
          type="month"
          bsSize="sm"
          style={{ width: 170 }}
          value={month}
          aria-label="PAD overview month"
          onChange={(e) => e.target.value && setMonth(e.target.value)}
        />
      </header>
      <div className="rdc-section__body">
        <div className="pad-ov-kpis">
          <KpiTile label={`Incentives given · ${t?.incentives_count || 0} pull${t?.incentives_count === 1 ? "" : "s"}`} value={t ? money(t.incentives_total) : "—"} accent={HUES.incentives} loading={loading} />
          <KpiTile label="PAD delays" value={t?.delays ?? "—"} accent={HUES.delays} loading={loading} />
          <KpiTile label="Temporary stops in force" value={t?.temp_stops ?? "—"} accent={HUES.tempStops} loading={loading} />
          <KpiTile label="Pulls skipped (stops + temp stops)" value={t?.skipped_pulls ?? "—"} accent={HUES.skipped} loading={loading} />
          <KpiTile label="New PADs recorded" value={t?.new_pads ?? "—"} accent={HUES.newPads} loading={loading} />
        </div>

        <div className="pad-ov-grid">
          <ChartFrame
            title="Largest incentives"
            subtitle="Dollars discounted this month, by property"
            loading={loading}
            error={error && error !== "forbidden" ? error : null}
            isEmpty={!loading && incentiveBars.length === 0}
            emptyTitle="No incentives this month"
            emptyBody="No move-in deals discount a pull in this month."
          >
            <div>
              <PropertyBars rows={incentiveBars} hue={HUES.incentives} valueFormatter={(v) => money(v)} />
              {topIncentives.length > 0 && (
                <ul className="pad-ov-list" style={{ marginTop: 6 }}>
                  {topIncentives.map((r, i) => (
                    <li key={i}>
                      <span className="who"><strong>{r.property_name}</strong> · Unit {r.unit_no}{r.tenant ? ` · ${r.tenant}` : ""}</span>
                      <span className="amt">{money(r.amount)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </ChartFrame>

          <ChartFrame
            title="PAD delays"
            subtitle="Delayed pulls landing in this month, by property"
            loading={loading}
            error={error && error !== "forbidden" ? error : null}
            isEmpty={!loading && delayBars.length === 0}
            emptyTitle="No PAD delays"
            emptyBody="Every pull lands on its regular day this month."
          >
            <PropertyBars rows={delayBars} hue={HUES.delays} />
          </ChartFrame>

          <ChartFrame
            title="Skipped pulls"
            subtitle="Stops and temporary stops — no money pulled, by property"
            loading={loading}
            error={error && error !== "forbidden" ? error : null}
            isEmpty={!loading && skippedBars.length === 0}
            emptyTitle="No skipped pulls"
            emptyBody="No stops or temporary stops are in force this month."
          >
            <div>
              <PropertyBars rows={skippedBars} hue={HUES.skipped} />
              {tempStops.length > 0 && (
                <ul className="pad-ov-list" style={{ marginTop: 6 }}>
                  {tempStops.slice(0, 6).map((r, i) => (
                    <li key={i}>
                      <span className="who"><strong>{r.property_name}</strong> · Unit {r.unit_no}{r.tenant ? ` · ${r.tenant}` : ""}</span>
                      <span style={{ whiteSpace: "nowrap", color: "#8A94A6" }}>{r.resume_date ? `resumes ${r.resume_date}` : "no resume date"}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </ChartFrame>
        </div>
      </div>
    </section>
  );
};

export default PadOverview;
